import { useState } from "react";
import { FileText, Loader2, X, Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const STATUS_BADGE = {
  low: "bg-[#E6F0EB] text-[#34745C]",
  medium: "bg-[#FFF4E5] text-[#B07020]",
  high: "bg-[#FCE8E4] text-[#B54D40]",
};

export default function ThreadSummaryPanel({ familyId, familyName, enabled, messageCount, onLockClick }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  // Only show button if there's enough thread to summarize
  if (messageCount < 5) return null;

  async function generate() {
    if (!enabled) {
      if (onLockClick) onLockClick();
      else toast.info("This is part of the AI Messaging Assistant add-on ($49/mo).");
      return;
    }
    setOpen(true);
    setLoading(true);
    try {
      const r = await api.post(`/messages/${familyId}/ai/summarize`);
      setSummary(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not summarize");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button
        data-testid="ai-summarize-btn"
        onClick={generate}
        type="button"
        className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${enabled ? "bg-[#D6E0D9]/40 text-[#2E5C50] hover:bg-[#D6E0D9]/70" : "bg-[#F0EDE8] text-[#57605A] hover:bg-[#E8E6E1]"}`}
        title={enabled ? "Summarize this thread" : "Requires AI Messaging Assistant add-on"}
      >
        {enabled ? <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Lock className="w-3.5 h-3.5" strokeWidth={1.5} />}
        Summarize
      </button>

      {open && (
        <div
          data-testid="ai-summary-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => !loading && setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-xl max-w-2xl w-full p-6 shadow-2xl max-h-[85vh] overflow-y-auto fade-in-up"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C07A60]">✨ AI Summary</p>
                <h3 className="mt-1 font-heading font-semibold text-lg text-[#1C211F]">
                  {familyName} thread
                </h3>
                {summary && <p className="text-xs text-[#57605A]">{summary.message_count} messages analyzed</p>}
              </div>
              <button onClick={() => setOpen(false)} className="text-[#57605A] hover:text-[#1C211F]" aria-label="Close">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            {loading && (
              <div className="py-10 flex flex-col items-center gap-3">
                <Loader2 className="w-6 h-6 text-[#2E5C50] animate-spin" />
                <p className="text-sm text-[#57605A]">Analyzing the conversation…</p>
              </div>
            )}

            {!loading && summary && (
              <div className="mt-5 space-y-5 text-sm">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide ${STATUS_BADGE[summary.status] || STATUS_BADGE.medium}`}>
                    {summary.status} attention
                  </span>
                  {summary.tone && <span className="text-xs text-[#57605A]">Tone: <em>{summary.tone}</em></span>}
                </div>

                {summary.highlights?.length > 0 && (
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-[#1C211F]">Highlights</h4>
                    <ul className="mt-2 space-y-1.5">
                      {summary.highlights.map((h, i) => (
                        <li key={i} data-testid={`summary-highlight-${i}`} className="text-sm text-[#1C211F] flex gap-2">
                          <span className="text-[#2E5C50]">•</span><span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {summary.action_items?.length > 0 && (
                  <div className="bg-[#FFF4E5]/40 border border-[#F0DCB8] rounded-md p-3">
                    <h4 className="font-heading font-semibold text-sm text-[#1C211F] flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 text-[#B07020]" strokeWidth={1.5} />
                      Action needed
                    </h4>
                    <ul className="mt-2 space-y-1.5">
                      {summary.action_items.map((a, i) => (
                        <li key={i} data-testid={`summary-action-${i}`} className="text-sm text-[#1C211F] flex gap-2">
                          <span className="text-[#C07A60]">→</span><span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {summary.open_issues?.length > 0 && (
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-[#1C211F]">Open issues</h4>
                    <ul className="mt-2 space-y-1.5">
                      {summary.open_issues.map((o, i) => (
                        <li key={i} className="text-sm text-[#57605A]">• {o}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
